import { useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { useProduct, useReorderProductImages, useUpdateProduct } from './useProducts';
import type { CategorySelectionItem } from '../components/CategorySelection';
import type { Product } from '../types';

function toNumberArray(arr: unknown[] | undefined): number[] | undefined {
  if (!arr || arr.length === 0) return undefined;
  return arr
    .map((id) => (typeof id === 'string' ? Number(id) : Number(id)))
    .filter((id) => !Number.isNaN(id));
}

function sameNumberList(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function sameNumberSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
}

function sameStringList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function normalizeComposition(
  composition: Array<{ value?: string; count?: number }> | undefined,
): Array<{ value: string; count: number }> {
  return (composition || [])
    .filter((item) => item?.value?.trim())
    .map((item) => ({
      value: String(item.value).trim(),
      count: Number(item.count) || 0,
    }));
}

function sameComposition(
  a: Array<{ value: string; count: number }>,
  b: Array<{ value: string; count: number }>,
): boolean {
  return (
    a.length === b.length &&
    a.every((item, i) => item.value === b[i].value && item.count === b[i].count)
  );
}

export function buildEditInitialValues(product: Product) {
  const categorySelections: CategorySelectionItem[] =
    product.categories && product.categories.length > 0
      ? product.categories.map((pc) => {
          const category = pc.category as any;
          const sectionId =
            category?.sectionId || category?.sections?.[0]?.sectionId || '';
          return {
            sectionId: sectionId as number | '',
            categoryId: pc.categoryId as number | '',
          };
        })
      : [{ sectionId: '' as const, categoryId: '' as const }];

  return {
    categorySelections,
    name: product.name || '',
    isNotFlower: product.isNotFlower ?? false,
    description: product.description || '',
    price: product.price,
    composition:
      Array.isArray(product.composition) && product.composition.length > 0
        ? product.composition
        : [{ value: '', count: 0 }],
    keywords: (product.keywords || []).slice(0, 20),
    height: product.height || undefined,
    width: product.width || undefined,
    weight: product.weight || undefined,
    flowerCount: product.flowerCount || undefined,
    flowerTypeIds: product.flowerTypes
      ? product.flowerTypes.map((pft) => pft.flowerTypeId)
      : product.flowerTypeId
        ? [product.flowerTypeId]
        : [],
    assemblyTypeId: product.assemblyTypeId ?? null,
    assemblyTimeMinutes: product.assemblyTimeMinutes ?? undefined,
    colorIds: product.colors
      ? product.colors.map((pc) => pc.colorId)
      : product.colorId
        ? [product.colorId]
        : [],
    images:
      product.images?.map((img) => ({
        id: img.id,
        url: img.cardUrl || img.url,
      })) || [],
  };
}

type EditSuccessResult = { wentToModeration: boolean };

export function useEditProductForm(
  productId: number,
  options?: {
    onSuccess?: (result?: EditSuccessResult) => void;
    onDone?: () => void;
  },
) {
  const { data: product, isLoading: productLoading } = useProduct(productId);
  const updateProduct = useUpdateProduct();
  const reorderImages = useReorderProductImages(productId);

  const initialValues = useMemo(
    () => (product ? buildEditInitialValues(product) : undefined),
    [product],
  );

  const handleSubmit = async (data: any) => {
    if (!product) return;

    try {
      const categoryIds =
        data.categorySelections
          ?.filter(
            (selection: any) => selection.categoryId && selection.categoryId !== '',
          )
          .map((selection: any) => Number(selection.categoryId)) || [];

      const composition = normalizeComposition(data.composition);
      const keywords =
        data.keywords && data.keywords.length > 0 ? data.keywords : undefined;
      const flowerTypeIds = toNumberArray(data.flowerTypeIds) ?? [];
      const colorIds = toNumberArray(data.colorIds) ?? [];
      const assemblyTypeId =
        data.assemblyTypeId && data.assemblyTypeId !== null
          ? Number(data.assemblyTypeId)
          : undefined;
      const assemblyTimeMinutes =
        data.assemblyTimeMinutes != null &&
        data.assemblyTimeMinutes !== '' &&
        Number(data.assemblyTimeMinutes) > 0
          ? Number(data.assemblyTimeMinutes)
          : null;

      const updateData = {
        name: data.name,
        isNotFlower: Boolean(data.isNotFlower ?? false),
        description: data.description,
        price: Number(data.price),
        categoryIds,
        composition,
        keywords,
        height: data.height ? Number(data.height) : undefined,
        width: data.width ? Number(data.width) : undefined,
        weight: data.weight ? Number(data.weight) : undefined,
        flowerCount:
          data.flowerCount != null && data.flowerCount !== ''
            ? Number(data.flowerCount)
            : null,
        flowerTypeIds: flowerTypeIds.length > 0 ? flowerTypeIds : undefined,
        assemblyTypeId,
        assemblyTimeMinutes,
        colorIds: colorIds.length > 0 ? colorIds : undefined,
      };

      const newFiles = data.images?.filter((img: any) => img instanceof File) ?? [];
      const files = newFiles.length > 0 ? newFiles : undefined;

      const originalImageIds = product.images?.map((img) => img.id) || [];
      const currentExistingImageIds: number[] =
        data.images
          ?.filter((img: any) => !(img instanceof File) && img?.id)
          .map((img: any) => Number(img.id)) || [];

      const deletedImageIds = originalImageIds.filter(
        (id) => !currentExistingImageIds.includes(id),
      );

      const orderChanged =
        sameNumberSet(originalImageIds, currentExistingImageIds) &&
        !sameNumberList(originalImageIds, currentExistingImageIds);

      const originalCategoryIds = (product.categories || []).map((c) => c.categoryId);
      const originalFlowerTypeIds = product.flowerTypes
        ? product.flowerTypes.map((pft) => pft.flowerTypeId)
        : product.flowerTypeId
          ? [product.flowerTypeId]
          : [];
      const originalColorIds = product.colors
        ? product.colors.map((pc) => pc.colorId)
        : product.colorId
          ? [product.colorId]
          : [];
      const originalComposition = normalizeComposition(
        Array.isArray(product.composition) ? product.composition : [],
      );
      const originalKeywords = product.keywords || [];
      const originalAssemblyTime =
        product.assemblyTimeMinutes != null && product.assemblyTimeMinutes > 0
          ? product.assemblyTimeMinutes
          : null;

      const contentUnchanged =
        updateData.name === (product.name || '') &&
        updateData.isNotFlower === Boolean(product.isNotFlower ?? false) &&
        (updateData.description || '') === (product.description || '') &&
        Number(updateData.price) === Number(product.price) &&
        sameNumberSet(categoryIds, originalCategoryIds) &&
        sameComposition(composition, originalComposition) &&
        sameStringList(keywords || [], originalKeywords) &&
        Number(updateData.height || 0) === Number(product.height || 0) &&
        Number(updateData.width || 0) === Number(product.width || 0) &&
        Number(updateData.weight || 0) === Number(product.weight || 0) &&
        Number(updateData.flowerCount || 0) === Number(product.flowerCount || 0) &&
        sameNumberSet(flowerTypeIds, originalFlowerTypeIds) &&
        (assemblyTypeId ?? null) === (product.assemblyTypeId ?? null) &&
        sameNumberSet(colorIds, originalColorIds);

      const assemblyTimeUnchanged = assemblyTimeMinutes === originalAssemblyTime;

      const onlyImageOrderChanged =
        contentUnchanged &&
        assemblyTimeUnchanged &&
        !files &&
        deletedImageIds.length === 0 &&
        orderChanged;

      if (onlyImageOrderChanged) {
        await reorderImages.mutateAsync(
          currentExistingImageIds.map((id, order) => ({ id, order })),
        );
        notifications.show({
          title: 'Успешно',
          message: 'Товар успешно обновлен',
          color: 'green',
        });
        options?.onDone?.();
        options?.onSuccess?.({ wentToModeration: false });
        return;
      }

      const onlyAssemblyTimeChanged =
        contentUnchanged &&
        !assemblyTimeUnchanged &&
        !files &&
        deletedImageIds.length === 0 &&
        !orderChanged;

      if (onlyAssemblyTimeChanged) {
        await updateProduct.mutateAsync({
          id: productId,
          data: { assemblyTimeMinutes },
        });
        notifications.show({
          title: 'Успешно',
          message: 'Товар успешно обновлен',
          color: 'green',
        });
        options?.onDone?.();
        options?.onSuccess?.({ wentToModeration: false });
        return;
      }

      await updateProduct.mutateAsync({
        id: productId,
        data: updateData,
        files,
        deletedImageIds: deletedImageIds.length > 0 ? deletedImageIds : undefined,
      });

      if (orderChanged && currentExistingImageIds.length > 0) {
        await reorderImages.mutateAsync(
          currentExistingImageIds.map((id, order) => ({ id, order })),
        );
      }

      notifications.show({
        title: 'Успешно',
        message: 'Товар успешно обновлен',
        color: 'green',
      });

      options?.onDone?.();
      options?.onSuccess?.({ wentToModeration: product.sellerId != null });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Произошла ошибка при обновлении товара';

      notifications.show({
        title: 'Ошибка',
        message: errorMessage,
        color: 'red',
      });
    }
  };

  return {
    product,
    productLoading,
    initialValues,
    handleSubmit,
    isSubmitting: updateProduct.isPending || reorderImages.isPending,
  };
}
